"use strict";
// This class implements docker commands using CLI, and
// it periodically monitors the docker daemon status.
// It manages containers defined in common.plugins.docker and could monitor other containers
var __classPrivateFieldSet = (this && this.__classPrivateFieldSet) || function (receiver, state, value, kind, f) {
    if (kind === "m") throw new TypeError("Private method is not writable");
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a setter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot write private member to an object whose class did not declare it");
    return (kind === "a" ? f.call(receiver, value) : f ? f.value = value : state.set(receiver, value)), value;
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
var _DockerManager_instances, _DockerManager_waitReady, _DockerManager_driver, _DockerManager_dockerode, _DockerManager_cliAvailable, _DockerManager_tarPack, _DockerManager_isDockerDaemonRunning, _DockerManager_exec, _DockerManager_isDockerInstalled, _DockerManager_isNeedSudo, _DockerManager_parseSize;
Object.defineProperty(exports, "__esModule", { value: true });
exports.parseLsLong = parseLsLong;
const node_util_1 = require("node:util");
const node_child_process_1 = require("node:child_process");
const node_net_1 = require("node:net");
const node_stream_1 = require("node:stream");
const node_path_1 = require("node:path");
const dockerode_1 = __importDefault(require("dockerode"));
const execPromise = (0, node_util_1.promisify)(node_child_process_1.exec);
function size2string(size) {
    if (size < 1024) {
        return `${size} B`;
    }
    if (size < 1024 * 1024) {
        return `${(size / 1024).toFixed(2)} KB`;
    }
    if (size < 1024 * 1024 * 1024) {
        return `${(size / (1024 * 1024)).toFixed(2)} MB`;
    }
    return `${(size / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}
/** Parse the output of "ls -l" command into an array of LsEntry */
function parseLsLong(listing) {
    if (!listing) {
        return [];
    }
    // Find all Permission-Marks as "drwxrwxr-x+" or "-rw-r--r--"
    const permRegex = /[dl-][rwx-]{9}\+?/g;
    const matches = Array.from(listing.matchAll(permRegex));
    if (matches.length === 0) {
        return [];
    }
    const result = [];
    for (let i = 0; i < matches.length; i++) {
        const start = matches[i].index;
        const end = i + 1 < matches.length ? matches[i + 1].index : listing.length;
        const entryText = listing.slice(start, end).trim();
        // Split in Felder, Name kann Leerzeichen enthalten, so Rest als Name nehmen
        const parts = entryText.split(/\s+/);
        if (parts.length < 9) {
            continue;
        }
        const permissions = parts[0];
        const links = Number.isFinite(Number(parts[1])) ? parseInt(parts[1], 10) : undefined;
        const owner = parts[2];
        const group = parts[3];
        const size = parseInt(parts[4], 10) || 0;
        const month = parts[5];
        const day = parts[6];
        const timeOrYear = parts[7];
        const name = parts.slice(8).join(' ');
        result.push({
            name,
            permissions,
            links,
            owner,
            group,
            size,
            rawDate: `${month} ${day} ${timeOrYear}`,
            isDir: permissions.charAt(0) === 'd',
            isLink: permissions.charAt(0) === 'l',
        });
    }
    return result;
}
class DockerManager {
    constructor(options) {
        _DockerManager_instances.add(this);
        this.installed = false;
        this.dockerVersion = '';
        this.needSudo = false;
        _DockerManager_waitReady.set(this, void 0);
        _DockerManager_driver.set(this, 'cli');
        _DockerManager_dockerode.set(this, null);
        _DockerManager_cliAvailable.set(this, false);
        _DockerManager_tarPack.set(this, null);
        this.log = options.logger;
        this.namespace = options.namespace;
        this.dockerApi = options.dockerApi;
        __classPrivateFieldSet(this, _DockerManager_waitReady, new Promise(resolve => this.init().then(() => resolve())), "f");
    }
    /** Wait till the check if docker is installed and the daemon is running is ready */
    isReady() {
        return __classPrivateFieldGet(this, _DockerManager_waitReady, "f");
    }
    /**
     * Get information about the Docker daemon: is it running and which version
     *
     * @returns Object with a version and daemonRunning
     */
    async getDockerDaemonInfo() {
        await this.isReady();
        const daemonRunning = await __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_isDockerDaemonRunning).call(this);
        return {
            version: this.dockerVersion,
            daemonRunning,
            removeSupported: !__classPrivateFieldGet(this, _DockerManager_dockerode, "f") || __classPrivateFieldGet(this, _DockerManager_cliAvailable, "f"),
            driver: __classPrivateFieldGet(this, _DockerManager_driver, "f"),
        };
    }
    static checkDockerSocket() {
        return new Promise(resolve => {
            const socket = (0, node_net_1.createConnection)({ path: '/var/run/docker.sock' }, () => {
                socket.end();
                resolve(true);
            });
            socket.on('error', e => {
                console.error(`Cannot connect to docker socket: ${e.message}`);
                resolve(false);
            });
        });
    }
    static async isDockerApiRunningOnPort(port, host = 'localhost') {
        return new Promise(resolve => {
            const socket = (0, node_net_1.createConnection)({ port, host }, () => {
                socket.write('GET /version HTTP/1.0\r\nHost: localhost\r\n\r\n');
            });
            let data = '';
            socket.on('data', chunk => (data += chunk.toString()));
            socket.on('end', () => {
                resolve(data.includes('Docker') || data.includes('Api-Version'));
            });
            socket.on('error', () => resolve(false));
        });
    }
    async init() {
        // first of all detects which way is available:
        // - '/var/run/docker.sock',
        // - http://localhost:2375,
        // - https://localhost:2376 or
        // - CLI
        // Probe the socket
        if (this.dockerApi && this.dockerApi.host && this.dockerApi.port) {
            __classPrivateFieldSet(this, _DockerManager_driver, this.dockerApi.protocol || 'http', "f");
            __classPrivateFieldSet(this, _DockerManager_dockerode, new dockerode_1.default({
                host: this.dockerApi.host,
                port: this.dockerApi.port,
                protocol: this.dockerApi.protocol || 'http',
            }), "f");
        }
        else if (await DockerManager.checkDockerSocket()) {
            __classPrivateFieldSet(this, _DockerManager_driver, 'socket', "f");
            __classPrivateFieldSet(this, _DockerManager_dockerode, new dockerode_1.default({ socketPath: '/var/run/docker.sock' }), "f");
        }
        else if (await DockerManager.isDockerApiRunningOnPort(2375)) {
            __classPrivateFieldSet(this, _DockerManager_driver, 'http', "f");
            __classPrivateFieldSet(this, _DockerManager_dockerode, new dockerode_1.default({ protocol: 'http', host: '127.0.0.1', port: 2375 }), "f");
        }
        else if (await DockerManager.isDockerApiRunningOnPort(2376)) {
            __classPrivateFieldSet(this, _DockerManager_driver, 'http', "f");
            __classPrivateFieldSet(this, _DockerManager_dockerode, new dockerode_1.default({ protocol: 'http', host: '127.0.0.1', port: 2376 }), "f");
        }
        else {
            __classPrivateFieldSet(this, _DockerManager_driver, 'cli', "f");
            __classPrivateFieldSet(this, _DockerManager_cliAvailable, true, "f");
        }
        if (!__classPrivateFieldGet(this, _DockerManager_cliAvailable, "f")) {
            try {
                const result = await execPromise('docker --version');
                if (!result.stderr && result.stdout) {
                    __classPrivateFieldSet(this, _DockerManager_cliAvailable, true, "f");
                }
            }
            catch (_a) {
                // ignore
            }
        }
        const version = await __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_isDockerInstalled).call(this);
        this.installed = !!version;
        if (version) {
            this.dockerVersion = version;
        }
        else {
            const daemonRunning = await __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_isDockerDaemonRunning).call(this);
            if (daemonRunning) {
                // Docker daemon is running, but docker command not found
                this.log.warn('Docker daemon is running, but docker command not found. May be "iobroker" user has no access to Docker. Run "iob fix" command to fix it.');
            }
            else {
                this.log.warn('Docker is not installed. Please install Docker.');
            }
        }
        if (this.installed) {
            // we still must check the sudo as autocompletion works only via CLI
            this.needSudo = await __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_isNeedSudo).call(this);
        }
    }
    getDefaultContainerName() {
        return `iob_${this.namespace.replace(/[-.]/g, '_')}`;
    }
    async containerGetRamAndCpuUsage(containerNameOrId) {
        try {
            const { stdout } = await __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_exec).call(this, `stats ${containerNameOrId} --no-stream --format "{{.CPUPerc}};{{.MemUsage}};{{.NetIO}};{{.BlockIO}};{{.PIDs}}"`);
            // Example: "0.15%;12.34MiB / 512MiB;1.2kB / 2.3kB;0B / 0B;5"
            const [cpuStr, memStr, netStr, blockIoStr, pid] = stdout.trim().split(';');
            const [memUsed, memMax] = memStr.split('/').map(it => it.trim());
            const [netRead, netWrite] = netStr.split('/').map(it => it.trim());
            const [blockIoRead, blockIoWrite] = blockIoStr.split('/').map(it => it.trim());
            return {
                ts: Date.now(),
                cpu: parseFloat(cpuStr.replace('%', '').replace(',', '.')),
                memUsed: __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_parseSize).call(this, memUsed.replace('iB', 'B')),
                memMax: __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_parseSize).call(this, memMax.replace('iB', 'B')),
                netRead: __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_parseSize).call(this, netRead.replace('iB', 'B')),
                netWrite: __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_parseSize).call(this, netWrite.replace('iB', 'B')),
                processes: parseInt(pid, 10),
                blockIoRead: __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_parseSize).call(this, blockIoRead.replace('iB', 'B')),
                blockIoWrite: __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_parseSize).call(this, blockIoWrite.replace('iB', 'B')),
            };
        }
        catch (e) {
            this.log.debug(`Cannot get stats: ${e.message}`);
            return null;
        }
    }
    /**
     * Update the image if a newer version is available
     *
     * @param image Image name with tag
     * @param ignoreIfNotExist If true, do not throw error if image does not exist
     * @returns New image info if image was updated, null if no update was necessary
     */
    async imageUpdate(image, ignoreIfNotExist) {
        const list = await this.imageList();
        if (!image.includes(':')) {
            image += ':latest';
        }
        const existingImage = list.find(it => `${it.repository}:${it.tag}` === image);
        if (!existingImage && !ignoreIfNotExist) {
            throw new Error(`Image ${image} not found`);
        }
        // Pull the image
        const result = await this.imagePull(image);
        if (result.stderr) {
            throw new Error(`Cannot pull image ${image}: ${result.stderr}`);
        }
        const newList = await this.imageList();
        const newImage = newList.find(it => `${it.repository}:${it.tag}` === image);
        if (!newImage) {
            throw new Error(`Image ${image} not found after pull`);
        }
        // If image ID has changed, the image was updated
        return !existingImage || existingImage.id !== newImage.id ? newImage : null;
    }
    /** Get disk usage information */
    async discUsage() {
        var _a;
        if (__classPrivateFieldGet(this, _DockerManager_dockerode, "f")) {
            const info = await __classPrivateFieldGet(this, _DockerManager_dockerode, "f").df();
            const result = { total: { size: 0, reclaimable: 0 } };
            if (info.Images) {
                let size = 0;
                let reclaimable = 0;
                for (const image of info.Images) {
                    size += image.Size;
                    reclaimable += image.SharedSize + image.VirtualSize;
                }
                result.images = {
                    total: info.Images.length,
                    active: info.Images.filter(img => img.Containers > 0).length,
                    size,
                    reclaimable,
                };
                result.total.size += size;
                result.total.reclaimable += reclaimable;
            }
            if (info.Containers) {
                let size = 0;
                for (const container of info.Containers) {
                    size += container.SizeRootFs || 0;
                }
                result.containers = {
                    total: info.Containers.length,
                    // @ts-expect-error todo
                    active: info.Containers.filter(cont => cont.State === 'running').length,
                    size,
                    reclaimable: 0, // Not available
                };
                result.total.size += size;
            }
            if (info.Volumes) {
                let size = 0;
                for (const volume of info.Volumes) {
                    size += ((_a = volume.UsageData) === null || _a === void 0 ? void 0 : _a.Size) || 0;
                }
                result.volumes = {
                    total: info.Volumes.length,
                    active: info.Volumes.filter(vol => { var _a; return ((_a = vol.UsageData) === null || _a === void 0 ? void 0 : _a.RefCount) && vol.UsageData.RefCount > 0; }).length,
                    size,
                    reclaimable: 0, // Not available
                };
                result.total.size += size;
            }
            // Build cache is not available
            return result;
        }
        const { stdout } = await __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_exec).call(this, `system df`);
        const result = { total: { size: 0, reclaimable: 0 } };
        // parse the output
        // TYPE            TOTAL     ACTIVE    SIZE      RECLAIMABLE
        // Images          2         1         2.715GB   2.715GB (99%)
        // Containers      1         1         26.22MB   0B (0%)
        // Local Volumes   0         0         0B        0B
        // Build Cache     0         0         0B        0B
        const lines = stdout.split('\n');
        for (const line of lines) {
            const parts = line.trim().split(/\s+/);
            if (parts.length >= 5 && parts[0] !== 'TYPE') {
                let size;
                let reclaimable;
                if (parts[0] === 'Images') {
                    const sizeStr = parts[3];
                    const reclaimableStr = parts[4].split(' ')[0];
                    size = __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_parseSize).call(this, sizeStr);
                    reclaimable = __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_parseSize).call(this, reclaimableStr);
                    result.images = {
                        total: parseInt(parts[1], 10),
                        active: parseInt(parts[2], 10),
                        size,
                        reclaimable: reclaimable,
                    };
                }
                else if (parts[0] === 'Containers') {
                    const sizeStr = parts[3];
                    const reclaimableStr = parts[4].split(' ')[0];
                    size = __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_parseSize).call(this, sizeStr);
                    reclaimable = __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_parseSize).call(this, reclaimableStr);
                    result.containers = {
                        total: parseInt(parts[1], 10),
                        active: parseInt(parts[2], 10),
                        size,
                        reclaimable: reclaimable,
                    };
                }
                else if (parts[0] === 'Local' && parts[1] === 'Volumes') {
                    const sizeStr = parts[4];
                    const reclaimableStr = parts[5].split(' ')[0];
                    size = __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_parseSize).call(this, sizeStr);
                    reclaimable = __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_parseSize).call(this, reclaimableStr);
                    result.volumes = {
                        total: parseInt(parts[2], 10),
                        active: parseInt(parts[3], 10),
                        size,
                        reclaimable: reclaimable,
                    };
                }
                else if (parts[0] === 'Build' && parts[1] === 'Cache') {
                    const sizeStr = parts[4];
                    const reclaimableStr = parts[5].split(' ')[0];
                    size = __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_parseSize).call(this, sizeStr);
                    reclaimable = __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_parseSize).call(this, reclaimableStr);
                    result.buildCache = {
                        total: parseInt(parts[2], 10),
                        active: parseInt(parts[3], 10),
                        size,
                        reclaimable: reclaimable,
                    };
                }
                result.total.size += size || 0;
                result.total.reclaimable += reclaimable || 0;
            }
        }
        return result;
    }
    /** Pull an image from the registry */
    async imagePull(image) {
        if (!image.includes(':')) {
            image += ':latest';
        }
        if (__classPrivateFieldGet(this, _DockerManager_dockerode, "f")) {
            const stream = await __classPrivateFieldGet(this, _DockerManager_dockerode, "f").pull(image);
            if (!stream) {
                throw new Error('No stream returned');
            }
            return new Promise((resolve, reject) => {
                const onFinished = (err) => {
                    if (err) {
                        return reject(err);
                    }
                    this.imageList()
                        .then(images => resolve({ stdout: `Image ${image} pulled`, stderr: '', images }))
                        .catch(reject);
                };
                const lastShownProgress = {};
                const onProgress = (event) => {
                    // {"status":"Downloading","progressDetail":{"current":109494080,"total":689664036},"progress":"[=======> ]  109.5MB/689.7MB","id":"29bce3058cea"}
                    // {"status":"Download complete","progressDetail":{},"id":"6859c690a072"}
                    // {"status":"Verifying Checksum","progressDetail":{},"id":"6859c690a072"}
                    // {"status":"Extracting","progressDetail":{"current":32,"total":32},"progress":"[======>] 32B/32B","id":"4f4fb700ef54"}
                    // {"status":"Pull complete","progressDetail":{},"id":"4f4fb700ef54"}
                    if (!lastShownProgress || Date.now() - lastShownProgress[event.id] > 4000) {
                        if (event.status === 'Download complete' ||
                            event.status === 'Pull complete' ||
                            event.status === 'Verifying Checksum') {
                            this.log.debug(`Image ${image}/${event.id}: ${event.status}`);
                        }
                        else if (event.status === 'Downloading' || event.status === 'Extracting') {
                            this.log.debug(`Pulling image ${image}/${event.id}: ${event.status} ${Math.round((event.progressDetail.current / event.progressDetail.total) * 1000) / 10}% of ${size2string(event.progressDetail.total)}`);
                        }
                        else {
                            this.log.debug(`Pulling image ${image}/${event.id}: ${JSON.stringify(event)}`);
                        }
                        lastShownProgress[event.id] = Date.now();
                    }
                };
                __classPrivateFieldGet(this, _DockerManager_dockerode, "f").modem.followProgress(stream, onFinished, onProgress);
            });
        }
        try {
            const result = await __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_exec).call(this, `pull ${image}`);
            const images = await this.imageList();
            if (!images.find(it => `${it.repository}:${it.tag}` === image)) {
                throw new Error(`Image ${image} not found after pull`);
            }
            return Object.assign(Object.assign({}, result), { images });
        }
        catch (e) {
            return { stdout: '', stderr: e.message.toString() };
        }
    }
    /** Autocomplete image names from Docker Hub */
    async imageNameAutocomplete(partialName) {
        try {
            // Read stars and descriptions
            const { stdout } = await __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_exec).call(this, `search ${partialName} --format "{{.Name}};{{.Description}};{{.IsOfficial}};{{.StarCount}}" --limit 50`);
            return stdout
                .split('\n')
                .filter(line => line.trim() !== '')
                .map(line => {
                const [name, description, isOfficial, starCount] = line.split(';');
                return {
                    name,
                    description,
                    isOfficial: isOfficial === 'true',
                    starCount: parseInt(starCount, 10) || 0,
                };
            });
        }
        catch (e) {
            this.log.debug(`Cannot search images: ${e.message}`);
            return [];
        }
    }
    static getDockerodeConfig(config) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z;
        let mounts;
        if (config.mounts) {
            for (const mount of config.mounts) {
                let volumeOptions;
                if (mount.volumeOptions) {
                    if (mount.volumeOptions.nocopy !== undefined) {
                        volumeOptions || (volumeOptions = {});
                        volumeOptions.NoCopy = mount.volumeOptions.nocopy;
                    }
                    if (mount.volumeOptions.labels) {
                        volumeOptions || (volumeOptions = {});
                        volumeOptions.Labels = mount.volumeOptions.labels;
                    }
                }
                let bindOptions;
                if (mount.bindOptions) {
                    if (mount.bindOptions.propagation) {
                        bindOptions || (bindOptions = {});
                        bindOptions.Propagation = mount.bindOptions.propagation;
                    }
                }
                let tmpfsOptions;
                if (mount.tmpfsOptions) {
                    if (mount.tmpfsOptions.size !== undefined) {
                        tmpfsOptions || (tmpfsOptions = {});
                        tmpfsOptions.SizeBytes = mount.tmpfsOptions.size;
                    }
                    if (mount.tmpfsOptions.mode !== undefined) {
                        tmpfsOptions || (tmpfsOptions = {});
                        tmpfsOptions.Mode = mount.tmpfsOptions.mode;
                    }
                }
                if (mount.source === true || mount.source === 'true') {
                    throw new Error(`Mount source must be a string, but got boolean true`);
                }
                const m = {
                    Target: mount.target,
                    Source: mount.source || '',
                    Type: mount.type,
                    ReadOnly: mount.readOnly,
                    Consistency: mount.consistency,
                    VolumeOptions: volumeOptions,
                    BindOptions: bindOptions,
                    TmpfsOptions: tmpfsOptions,
                };
                mounts || (mounts = []);
                mounts.push(m);
            }
        }
        if (!config.name) {
            throw new Error(`Container name must be a string, but got boolean true`);
        }
        return {
            name: config.name,
            Image: config.image,
            Cmd: Array.isArray(config.command) && config.command.length
                ? config.command
                : typeof config.command === 'string'
                    ? [config.command]
                    : undefined,
            Entrypoint: config.entrypoint,
            Env: config.environment
                ? Object.keys(config.environment)
                    .map(key => (config.environment ? `${key}=${config.environment[key]}` : ''))
                    .filter(e => e)
                : undefined,
            // WorkingDir: config.workingDir,
            // { '/data': {} }
            Labels: config.labels,
            ExposedPorts: config.ports
                ? config.ports.reduce((acc, port) => {
                    acc[`${port.containerPort}/${port.protocol || 'tcp'}`] = {};
                    return acc;
                }, {})
                : undefined,
            HostConfig: {
                // https://github.com/apocas/dockerode/issues/265#issuecomment-462786936
                Binds: config.volumes,
                PortBindings: config.ports
                    ? config.ports.reduce((acc, port) => {
                        acc[`${port.containerPort}/${port.protocol || 'tcp'}`] = [
                            {
                                HostPort: port.hostPort ? port.hostPort.toString() : undefined,
                                HostIp: port.hostIP || undefined,
                            },
                        ];
                        return acc;
                    }, {})
                    : undefined,
                Mounts: mounts,
                NetworkMode: config.networkMode === true || config.networkMode === 'true' ? '' : config.networkMode || undefined,
                // Links: config.links,
                // Dns: config.dns,
                // DnsOptions: config.dnsOptions,
                // DnsSearch: config.dnsSearch,
                ExtraHosts: config.extraHosts,
                // VolumesFrom: config.volumesFrom,
                Privileged: (_a = config.security) === null || _a === void 0 ? void 0 : _a.privileged,
                CapAdd: (_b = config.security) === null || _b === void 0 ? void 0 : _b.capAdd,
                CapDrop: (_c = config.security) === null || _c === void 0 ? void 0 : _c.capDrop,
                UsernsMode: (_d = config.security) === null || _d === void 0 ? void 0 : _d.usernsMode,
                IpcMode: (_e = config.security) === null || _e === void 0 ? void 0 : _e.ipc,
                PidMode: (_f = config.security) === null || _f === void 0 ? void 0 : _f.pid,
                GroupAdd: (_h = (_g = config.security) === null || _g === void 0 ? void 0 : _g.groupAdd) === null || _h === void 0 ? void 0 : _h.map(g => g.toString()),
                ReadonlyRootfs: config.readOnly,
                RestartPolicy: {
                    Name: ((_j = config.restart) === null || _j === void 0 ? void 0 : _j.policy) || 'no',
                    MaximumRetryCount: ((_k = config.restart) === null || _k === void 0 ? void 0 : _k.maxRetries) || 0,
                },
                CpuShares: (_l = config.resources) === null || _l === void 0 ? void 0 : _l.cpuShares,
                CpuPeriod: (_m = config.resources) === null || _m === void 0 ? void 0 : _m.cpuPeriod,
                CpuQuota: (_o = config.resources) === null || _o === void 0 ? void 0 : _o.cpuQuota,
                CpusetCpus: (_q = (_p = config.resources) === null || _p === void 0 ? void 0 : _p.cpus) === null || _q === void 0 ? void 0 : _q.toString(),
                Memory: (_r = config.resources) === null || _r === void 0 ? void 0 : _r.memory,
                MemorySwap: (_s = config.resources) === null || _s === void 0 ? void 0 : _s.memorySwap,
                MemoryReservation: (_t = config.resources) === null || _t === void 0 ? void 0 : _t.memoryReservation,
                ShmSize: (_u = config.resources) === null || _u === void 0 ? void 0 : _u.shmSize,
                // OomKillDisable: config.resources?.oomKillDisable,
                // OomScoreAdj: config.resources?.oomScoreAdj,
                LogConfig: config.logging
                    ? {
                        Type: config.logging.driver || 'json-file',
                        Config: config.logging.options || {},
                    }
                    : undefined,
                SecurityOpt: [
                    ...(((_v = config.security) === null || _v === void 0 ? void 0 : _v.seccomp) ? [`seccomp=${config.security.seccomp}`] : []),
                    ...(((_w = config.security) === null || _w === void 0 ? void 0 : _w.apparmor) ? [`apparmor=${config.security.apparmor}`] : []),
                    ...(((_x = config.security) === null || _x === void 0 ? void 0 : _x.noNewPrivileges) ? ['no-new-privileges:true'] : []),
                ],
                Sysctls: config.sysctls,
                Init: config.init,
            },
            StopSignal: (_y = config.stop) === null || _y === void 0 ? void 0 : _y.signal,
            StopTimeout: (_z = config.stop) === null || _z === void 0 ? void 0 : _z.gracePeriodSec,
            Tty: config.tty,
            OpenStdin: config.openStdin,
        };
    }
    /**
     * Create and start a container with the given configuration. No checks are done.
     */
    async containerRun(config) {
        var _a;
        if (__classPrivateFieldGet(this, _DockerManager_dockerode, "f")) {
            const outStream = new node_stream_1.PassThrough();
            const errStream = new node_stream_1.PassThrough();
            let stdout = '';
            let stderr = '';
            outStream.on('data', chunk => (stdout += chunk.toString()));
            errStream.on('data', chunk => (stderr += chunk.toString()));
            const dockerConfig = DockerManager.getDockerodeConfig(config);
            await __classPrivateFieldGet(this, _DockerManager_dockerode, "f").run(config.image, !((_a = config.command) === null || _a === void 0 ? void 0 : _a.length) ? [] : Array.isArray(config.command) ? config.command : [config.command], [outStream, errStream], Object.assign(Object.assign({}, dockerConfig), { Tty: false }));
            outStream.end();
            errStream.end();
            return { stdout, stderr };
        }
        try {
            return await __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_exec).call(this, `run ${DockerManager.toDockerRun(config)}`);
        }
        catch (e) {
            return { stdout: '', stderr: e.message.toString() };
        }
    }
    /**
     * Create a container with the given configuration without starting it. No checks are done.
     */
    async containerCreate(config) {
        if (__classPrivateFieldGet(this, _DockerManager_dockerode, "f")) {
            const container = await __classPrivateFieldGet(this, _DockerManager_dockerode, "f").createContainer(DockerManager.getDockerodeConfig(config));
            return { stdout: `Container ${container.id} created`, stderr: '' };
        }
        try {
            return await __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_exec).call(this, `create ${DockerManager.toDockerRun(config, true)}`);
        }
        catch (e) {
            return { stdout: '', stderr: e.message.toString() };
        }
    }
    /**
     * Recreate a container
     *
     * This function checks if a container is running, stops it if necessary,
     * removes it and creates a new one with the given configuration.
     * The container is not started after creation.
     *
     * @param config new configuration
     * @returns stdout and stderr of the create command
     */
    async containerReCreate(config) {
        if (__classPrivateFieldGet(this, _DockerManager_dockerode, "f")) {
            // Get if the container is running
            let containers = await this.containerList();
            // find ID of container
            const containerInfo = containers.find(it => it.names === config.name);
            if (containerInfo) {
                const container = __classPrivateFieldGet(this, _DockerManager_dockerode, "f").getContainer(containerInfo.id);
                if (containerInfo.status === 'running' || containerInfo.status === 'restarting') {
                    await container.stop();
                    containers = await this.containerList();
                    if (containers.find(it => it.id === containerInfo.id && it.status === 'running')) {
                        this.log.warn(`Cannot remove container: still running`);
                        throw new Error(`Container ${containerInfo.id} still running after stop`);
                    }
                }
                // Remove container
                await container.remove();
                containers = await this.containerList();
                if (containers.find(it => it.id === containerInfo.id)) {
                    this.log.warn(`Cannot remove container: still existing`);
                    throw new Error(`Container ${containerInfo.id} still found after remove`);
                }
            }
            const newContainer = await __classPrivateFieldGet(this, _DockerManager_dockerode, "f").createContainer(DockerManager.getDockerodeConfig(config));
            return { stdout: `Container ${newContainer.id} created`, stderr: '' };
        }
        try {
            // Get if the container is running
            let containers = await this.containerList();
            // find ID of container
            const containerInfo = containers.find(it => it.names === config.name);
            if (containerInfo) {
                if (containerInfo.status === 'running' || containerInfo.status === 'restarting') {
                    const stopResult = await __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_exec).call(this, `stop ${containerInfo.id}`);
                    containers = await this.containerList();
                    if (containers.find(it => it.id === containerInfo.id && it.status === 'running')) {
                        this.log.warn(`Cannot remove container: ${stopResult.stderr || stopResult.stdout}`);
                        throw new Error(`Container ${containerInfo.id} still running after stop`);
                    }
                }
                // Remove container
                const rmResult = await __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_exec).call(this, `rm ${containerInfo.id}`);
                containers = await this.containerList();
                if (containers.find(it => it.id === containerInfo.id)) {
                    this.log.warn(`Cannot remove container: ${rmResult.stderr || rmResult.stdout}`);
                    throw new Error(`Container ${containerInfo.id} still found after remove`);
                }
            }
            return await __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_exec).call(this, `create ${DockerManager.toDockerRun(config, true)}`);
        }
        catch (e) {
            return { stdout: '', stderr: e.message.toString() };
        }
    }
    async containerCreateCompose(compose) {
        try {
            return await __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_exec).call(this, `compose -f ${compose} create`);
        }
        catch (e) {
            return { stdout: '', stderr: e.message.toString() };
        }
    }
    /** List all images */
    async imageList() {
        if (__classPrivateFieldGet(this, _DockerManager_dockerode, "f")) {
            const images = await __classPrivateFieldGet(this, _DockerManager_dockerode, "f").listImages();
            return images.map(img => {
                const repoTag = img.RepoTags && img.RepoTags.length ? img.RepoTags[0] : '<none>:<none>';
                const [repository, tag] = repoTag.split(':');
                return {
                    repository,
                    tag,
                    id: img.Id.startsWith('sha256:') ? img.Id.substring(7, 19) : img.Id.substring(0, 12),
                    createdSince: new Date(img.Created * 1000).toISOString(),
                    size: img.Size,
                };
            });
        }
        try {
            const { stdout } = await __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_exec).call(this, 'images --format "{{.Repository}}:{{.Tag}};{{.ID}};{{.CreatedAt}};{{.Size}}"');
            return stdout
                .split('\n')
                .filter(line => line.trim() !== '')
                .map(line => {
                const [repositoryTag, id, createdSince, size] = line.split(';');
                const [repository, tag] = repositoryTag.split(':');
                return {
                    repository,
                    tag,
                    id,
                    createdSince,
                    size: __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_parseSize).call(this, size),
                };
            });
        }
        catch (e) {
            this.log.debug(`Cannot list images: ${e.message}`);
            return [];
        }
    }
    /** Build an image from a Dockerfile */
    async imageBuild(dockerfilePath, tag) {
        if (__classPrivateFieldGet(this, _DockerManager_dockerode, "f")) {
            try {
                const stream = await __classPrivateFieldGet(this, _DockerManager_dockerode, "f").buildImage(dockerfilePath, {
                    t: tag,
                    dockerfile: dockerfilePath,
                });
                return new Promise((resolve, reject) => {
                    let stdout = '';
                    let stderr = '';
                    const onFinished = (err) => {
                        if (err) {
                            return reject(err);
                        }
                        resolve({ stdout, stderr });
                    };
                    const onProgress = (event) => {
                        if (event.stream) {
                            stdout += event.stream;
                        }
                        if (event.error) {
                            stderr += event.error;
                        }
                        this.log.debug(JSON.stringify(event));
                    };
                    __classPrivateFieldGet(this, _DockerManager_dockerode, "f").modem.followProgress(stream, onFinished, onProgress);
                });
            }
            catch (e) {
                return { stdout: '', stderr: e.message.toString() };
            }
        }
        try {
            return await __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_exec).call(this, `build -t ${tag} -f ${dockerfilePath} .`);
        }
        catch (e) {
            return { stdout: '', stderr: e.message.toString() };
        }
    }
    /** Tag an image with a new tag */
    async imageTag(imageId, newTag) {
        try {
            return await __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_exec).call(this, `tag ${imageId} ${newTag}`);
        }
        catch (e) {
            return { stdout: '', stderr: e.message.toString() };
        }
    }
    /** Remove an image */
    async imageRemove(imageId) {
        try {
            const result = await __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_exec).call(this, `rmi ${imageId}`);
            const images = await this.imageList();
            if (images.find(it => `${it.repository}:${it.tag}` === imageId)) {
                return { stdout: '', stderr: `Image ${imageId} still found after deletion`, images };
            }
            return Object.assign(Object.assign({}, result), { images });
        }
        catch (e) {
            return { stdout: '', stderr: e.message.toString() };
        }
    }
    static rodeInspect2DockerImageInspect(data) {
        return {
            Id: data.Id.startsWith('sha256:') ? data.Id.substring(7, 19) : data.Id.substring(0, 12),
            RepoTags: data.RepoTags,
            RepoDigests: data.RepoDigests,
            Parent: data.Parent,
            Comment: data.Comment,
            Created: data.Created,
            DockerVersion: data.DockerVersion,
            Author: data.Author,
            Architecture: data.Architecture,
            Os: data.Os,
            Size: data.Size,
            GraphDriver: {
                Data: data.GraphDriver.Data,
                Name: data.GraphDriver.Name,
            },
            RootFS: data.RootFS,
            Config: Object.assign(Object.assign({}, data.Config), { Entrypoint: Array.isArray(data.Config.Entrypoint)
                    ? data.Config.Entrypoint
                    : typeof data.Config.Entrypoint === 'string'
                        ? [data.Config.Entrypoint]
                        : [] }),
        };
    }
    /** Inspect an image */
    async imageInspect(imageId) {
        if (__classPrivateFieldGet(this, _DockerManager_dockerode, "f")) {
            const image = __classPrivateFieldGet(this, _DockerManager_dockerode, "f").getImage(imageId);
            const data = await image.inspect();
            return DockerManager.rodeInspect2DockerImageInspect(data);
        }
        try {
            const { stdout } = await __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_exec).call(this, `inspect ${imageId}`);
            return JSON.parse(stdout)[0];
        }
        catch (e) {
            this.log.debug(`Cannot inspect image: ${e.message.toString()}`);
            return null;
        }
    }
    async imagePrune() {
        if (__classPrivateFieldGet(this, _DockerManager_dockerode, "f")) {
            try {
                await __classPrivateFieldGet(this, _DockerManager_dockerode, "f").pruneImages();
                return { stdout: 'Unused images pruned', stderr: '' };
            }
            catch (e) {
                return { stdout: '', stderr: e.message.toString() };
            }
        }
        try {
            return await __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_exec).call(this, `image prune -f`);
        }
        catch (e) {
            return { stdout: '', stderr: e.message.toString() };
        }
    }
    /**
     * Stop a container
     *
     * @param container Container name or ID
     */
    async containerStop(container) {
        if (__classPrivateFieldGet(this, _DockerManager_dockerode, "f")) {
            let containers = await this.containerList();
            // find ID of container
            const containerInfo = containers.find(it => it.names === container || it.id === container);
            if (!containerInfo) {
                throw new Error(`Container ${container} not found`);
            }
            const dockerContainer = __classPrivateFieldGet(this, _DockerManager_dockerode, "f").getContainer(containerInfo.id);
            await dockerContainer.stop();
            containers = await this.containerList();
            if (containers.find(it => it.id === containerInfo.id && it.status === 'running')) {
                throw new Error(`Container ${container} still running after stop`);
            }
            return { stdout: `Container ${containerInfo.id} stopped`, stderr: '', containers };
        }
        try {
            let containers = await this.containerList();
            // find ID of container
            const containerInfo = containers.find(it => it.names === container || it.id === container);
            if (!containerInfo) {
                throw new Error(`Container ${container} not found`);
            }
            const result = await __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_exec).call(this, `stop ${containerInfo.id}`);
            containers = await this.containerList();
            if (containers.find(it => it.id === containerInfo.id && it.status === 'running')) {
                throw new Error(`Container ${container} still running after stop`);
            }
            return Object.assign(Object.assign({}, result), { containers });
        }
        catch (e) {
            return { stdout: '', stderr: e.message.toString() };
        }
    }
    /**
     * Start a container
     *
     * @param container Container name or ID
     */
    async containerStart(container) {
        if (__classPrivateFieldGet(this, _DockerManager_dockerode, "f")) {
            let containers = await this.containerList();
            // find ID of container
            const containerInfo = containers.find(it => it.names === container || it.id === container);
            if (!containerInfo) {
                throw new Error(`Container ${container} not found`);
            }
            const dockerContainer = __classPrivateFieldGet(this, _DockerManager_dockerode, "f").getContainer(containerInfo.id);
            await dockerContainer.start();
            containers = await this.containerList();
            if (containers.find(it => it.id === containerInfo.id && it.status !== 'running' && it.status !== 'restarting')) {
                throw new Error(`Container ${container} not running after start`);
            }
            return { stdout: `Container ${containerInfo.id} started`, stderr: '', containers };
        }
        try {
            let containers = await this.containerList();
            // find ID of container
            const containerInfo = containers.find(it => it.names === container || it.id === container);
            if (!containerInfo) {
                throw new Error(`Container ${container} not found`);
            }
            const result = await __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_exec).call(this, `start ${containerInfo.id}`);
            containers = await this.containerList();
            if (containers.find(it => it.id === containerInfo.id && it.status !== 'running' && it.status !== 'restarting')) {
                throw new Error(`Container ${container} not running after start`);
            }
            return Object.assign(Object.assign({}, result), { containers });
        }
        catch (e) {
            return { stdout: '', stderr: e.message.toString() };
        }
    }
    /**
     * Restart a container
     *
     * This function restarts a container by its name or ID.
     * It accepts an optional timeout in seconds to wait before killing the container (default is 5 seconds).
     *
     * @param container Container name or ID
     * @param timeoutSeconds Timeout in seconds to wait before killing the container (default: 5)
     */
    async containerRestart(container, timeoutSeconds) {
        container || (container = this.getDefaultContainerName());
        if (__classPrivateFieldGet(this, _DockerManager_dockerode, "f")) {
            const containers = await this.containerList();
            // find ID of container
            const containerInfo = containers.find(it => it.names === container || it.id === container);
            if (!containerInfo) {
                throw new Error(`Container ${container} not found`);
            }
            const dockerContainer = __classPrivateFieldGet(this, _DockerManager_dockerode, "f").getContainer(containerInfo.id);
            await dockerContainer.restart({ t: timeoutSeconds || 5 });
            return { stdout: `Container ${containerInfo.id} restarted`, stderr: '' };
        }
        try {
            const containers = await this.containerList();
            // find ID of container
            const containerInfo = containers.find(it => it.names === container || it.id === container);
            if (!containerInfo) {
                throw new Error(`Container ${container} not found`);
            }
            return await __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_exec).call(this, `restart -t ${timeoutSeconds || 5} ${containerInfo.id}`);
        }
        catch (e) {
            return { stdout: '', stderr: e.message.toString() };
        }
    }
    /** Find the IP address of a container, via which it can be reached from the host */
    async getIpOfContainer(containerName) {
        var _a;
        containerName || (containerName = this.getDefaultContainerName());
        const data = await this.containerInspect(containerName);
        if (!((_a = data === null || data === void 0 ? void 0 : data.NetworkSettings) === null || _a === void 0 ? void 0 : _a.Networks)) {
            throw new Error(`No network settings found for container ${containerName}`);
        }
        for (const n in data.NetworkSettings.Networks) {
            if (data.NetworkSettings.Networks[n].IPAddress) {
                return data.NetworkSettings.Networks[n].IPAddress;
            }
        }
        throw new Error(`No IP address found for container ${containerName}`);
    }
    /**
     * Remove the container and if necessary, stop it first
     *
     * @param container Container name or ID
     */
    async containerRemove(container) {
        if (__classPrivateFieldGet(this, _DockerManager_dockerode, "f")) {
            let containers = await this.containerList();
            const containerInfo = containers.find(it => it.names === container || it.id === container);
            if (!containerInfo) {
                throw new Error(`Container ${container} not found`);
            }
            const dockerContainer = __classPrivateFieldGet(this, _DockerManager_dockerode, "f").getContainer(containerInfo.id);
            if (containerInfo.status === 'running' || containerInfo.status === 'restarting') {
                await dockerContainer.stop();
            }
            await dockerContainer.remove();
            containers = await this.containerList();
            if (containers.find(it => it.id === containerInfo.id)) {
                throw new Error(`Container ${container} still found after remove`);
            }
            return { stdout: `Container ${containerInfo.id} removed`, stderr: '', containers };
        }
        try {
            let containers = await this.containerList();
            // find ID of container
            const containerInfo = containers.find(it => it.names === container || it.id === container);
            if (!containerInfo) {
                throw new Error(`Container ${container} not found`);
            }
            // ensure that the container is stopped
            if (containerInfo.status === 'running' || containerInfo.status === 'restarting') {
                // stop container
                const result = await __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_exec).call(this, `stop ${containerInfo.id}`);
                if (result.stderr) {
                    throw new Error(`Cannot stop container ${container}: ${result.stderr}`);
                }
            }
            const result = await __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_exec).call(this, `rm ${container}`);
            containers = await this.containerList();
            if (containers.find(it => it.id === containerInfo.id)) {
                throw new Error(`Container ${container} still found after remove`);
            }
            return Object.assign(Object.assign({}, result), { containers });
        }
        catch (e) {
            return { stdout: '', stderr: e.message.toString() };
        }
    }
    /**
     * List all containers
     *
     * @param all If true, list all containers. If false, the list only running containers. Default is true.
     */
    async containerList(all = true) {
        if (__classPrivateFieldGet(this, _DockerManager_dockerode, "f")) {
            const containers = await __classPrivateFieldGet(this, _DockerManager_dockerode, "f").listContainers({ all });
            return containers.map(cont => {
                const statusKey = cont.State.toLowerCase();
                let status;
                if (statusKey === 'up') {
                    status = 'running';
                }
                else if (statusKey === 'exited') {
                    status = 'exited';
                }
                else if (statusKey === 'created') {
                    status = 'created';
                }
                else if (statusKey === 'paused') {
                    status = 'paused';
                }
                else if (statusKey === 'restarting') {
                    status = 'restarting';
                }
                else {
                    status = statusKey;
                }
                // Try to convert: Up 6 minutes, Up 6 hours, Up 6 days to minutes
                const uptimeMatch = cont.Status.match(/Up (\d+) (seconds?|minutes?|hours?|days?)/);
                let minutes = 0;
                if (uptimeMatch) {
                    const value = parseInt(uptimeMatch[1], 10);
                    const unit = uptimeMatch[2];
                    if (unit.startsWith('hour')) {
                        minutes = value * 60;
                    }
                    else if (unit.startsWith('day')) {
                        minutes = value * 60 * 24;
                    }
                    else if (unit.startsWith('second')) {
                        minutes = Math.ceil(value / 60);
                    }
                }
                else if (cont.Status === 'Up About a minute') {
                    minutes = 1;
                }
                return {
                    id: cont.Id.substring(0, 12),
                    image: cont.Image,
                    command: cont.Command,
                    createdAt: new Date(cont.Created * 1000).toISOString(),
                    status,
                    uptime: minutes.toString(),
                    ports: cont.Ports.map(p => `${p.IP ? `${p.IP}:` : ''}${p.PublicPort ? `${p.PublicPort}->` : ''}${p.PrivatePort}/${p.Type}`).join(', '),
                    names: cont.Names.map(n => (n.startsWith('/') ? n.substring(1) : n)).join(', '),
                    labels: cont.Labels || {},
                };
            });
        }
        try {
            const { stdout } = await __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_exec).call(this, `ps ${all ? '-a' : ''} --format  "{{.Names}};{{.Status}};{{.ID}};{{.Image}};{{.Command}};{{.CreatedAt}};{{.Ports}};{{.Labels}}"`);
            return stdout
                .split('\n')
                .filter(line => line.trim() !== '')
                .map(line => {
                const [names, statusInfo, id, image, command, createdAt, ports, labels] = line.split(';');
                const [status, ...uptime] = statusInfo.split(' ');
                let statusKey = status.toLowerCase();
                if (statusKey === 'up') {
                    statusKey = 'running';
                }
                return {
                    id,
                    image,
                    command,
                    createdAt,
                    status: statusKey,
                    uptime: uptime.join(' '),
                    ports,
                    names,
                    labels: (labels === null || labels === void 0 ? void 0 : labels.split(',').reduce((acc, label) => {
                        const [key, value] = label.split('=');
                        if (key && value) {
                            acc[key] = value;
                        }
                        return acc;
                    }, {})) || {},
                };
            });
        }
        catch (e) {
            this.log.debug(`Cannot list containers: ${e.message}`);
            return [];
        }
    }
    /**
     * Get the logs of a container
     *
     * @param containerNameOrId Container name or ID
     * @param options Options for logs
     * @param options.tail Number of lines to show from the end of the logs
     * @param options.follow If true, follow the logs (not implemented yet)
     */
    async containerLogs(containerNameOrId, options = {}) {
        if (__classPrivateFieldGet(this, _DockerManager_dockerode, "f")) {
            try {
                const container = __classPrivateFieldGet(this, _DockerManager_dockerode, "f").getContainer(containerNameOrId);
                const data = await container.logs({
                    stdout: true,
                    stderr: true,
                    follow: false,
                    tail: options.tail || undefined,
                });
                return data
                    .toString()
                    .split('\n')
                    .filter(line => line.trim() !== '');
            }
            catch (e) {
                return e
                    .toString()
                    .split('\n')
                    .map((line) => line.trim());
            }
        }
        try {
            const args = [];
            if (options.tail !== undefined) {
                args.push(`--tail ${options.tail}`);
            }
            if (options.follow) {
                args.push(`--follow`);
                throw new Error('Follow option is not implemented yet');
            }
            const result = await __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_exec).call(this, `logs${args.length ? ` ${args.join(' ')}` : ''} ${containerNameOrId}`);
            return (result.stdout || result.stderr).split('\n').filter(line => line.trim() !== '');
        }
        catch (e) {
            return e
                .toString()
                .split('\n')
                .map((line) => line.trim());
        }
    }
    static dockerodeInspect2DockerContainerInspect(data) {
        return Object.assign({}, data);
    }
    /** Inspect a container */
    async containerInspect(containerNameOrId) {
        if (__classPrivateFieldGet(this, _DockerManager_dockerode, "f")) {
            try {
                const container = __classPrivateFieldGet(this, _DockerManager_dockerode, "f").getContainer(containerNameOrId);
                const dResult = await container.inspect();
                const result = DockerManager.dockerodeInspect2DockerContainerInspect(dResult);
                if (result.State.Running) {
                    result.Stats = (await this.containerGetRamAndCpuUsage(containerNameOrId)) || undefined;
                }
                return result;
            }
            catch (e) {
                this.log.debug(`Cannot inspect container: ${e.message.toString()}`);
                return null;
            }
        }
        try {
            const { stdout } = await __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_exec).call(this, `inspect ${containerNameOrId}`);
            const result = JSON.parse(stdout)[0];
            if (result.State.Running) {
                result.Stats = (await this.containerGetRamAndCpuUsage(containerNameOrId)) || undefined;
            }
            return result;
        }
        catch (e) {
            this.log.debug(`Cannot inspect container: ${e.message.toString()}`);
            return null;
        }
    }
    async containerPrune() {
        var _a;
        if (__classPrivateFieldGet(this, _DockerManager_dockerode, "f")) {
            try {
                const result = await __classPrivateFieldGet(this, _DockerManager_dockerode, "f").pruneContainers();
                return { stdout: `Containers pruned: ${((_a = result.ContainersDeleted) === null || _a === void 0 ? void 0 : _a.join(', ')) || 'none'}`, stderr: '' };
            }
            catch (e) {
                return { stdout: '', stderr: e.message.toString() };
            }
        }
        try {
            return await __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_exec).call(this, `container prune -f`);
        }
        catch (e) {
            return { stdout: '', stderr: e.message.toString() };
        }
    }
    /**
     * Build a docker run command string from ContainerConfig
     */
    static toDockerRun(config, create) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
        const args = [];
        // detach / interactive
        if (config.detach !== false && !create) {
            // default is true
            args.push('-d');
        }
        if (config.tty) {
            args.push('-t');
        }
        if (config.stdinOpen) {
            args.push('-i');
        }
        if (config.removeOnExit) {
            args.push('--rm');
        }
        // name
        if (config.name) {
            args.push('--name', config.name);
        }
        // hostname / domain
        if (config.hostname) {
            args.push('--hostname', config.hostname);
        }
        if (config.domainname) {
            args.push('--domainname', config.domainname);
        }
        // environment
        if (config.environment) {
            for (const [key, value] of Object.entries(config.environment)) {
                if (key && value) {
                    args.push('-e', `${key}=${value}`);
                }
            }
        }
        if (config.envFile) {
            for (const file of config.envFile) {
                args.push('--env-file', file);
            }
        }
        // labels
        if (config.labels) {
            for (const [key, value] of Object.entries(config.labels)) {
                args.push('--label', `${key}=${value}`);
            }
        }
        // ports
        if (config.publishAllPorts) {
            args.push('-P');
        }
        if (config.ports) {
            for (const p of config.ports) {
                if (!p.containerPort) {
                    continue;
                }
                const mapping = (p.hostIP ? `${p.hostIP}:` : '') +
                    (p.hostPort ? `${p.hostPort}:` : '') +
                    p.containerPort +
                    (p.protocol ? `/${p.protocol}` : '');
                args.push('-p', mapping);
            }
        }
        // volumes / mounts
        if (config.volumes) {
            for (const v of config.volumes) {
                args.push('-v', v);
            }
        }
        if (config.mounts) {
            for (const m of config.mounts) {
                let mount = `type=${m.type},target=${m.target}`;
                if (m.source) {
                    mount += `,source=${m.source}`;
                }
                if (m.readOnly) {
                    mount += `,readonly`;
                }
                args.push('--mount', mount);
            }
        }
        // restart policy
        if ((_a = config.restart) === null || _a === void 0 ? void 0 : _a.policy) {
            const val = config.restart.policy === 'on-failure' && config.restart.maxRetries
                ? `on-failure:${config.restart.maxRetries}`
                : config.restart.policy;
            args.push('--restart', val);
        }
        // user & workdir
        if (config.user) {
            args.push('--user', String(config.user));
        }
        if (config.workdir) {
            args.push('--workdir', config.workdir);
        }
        // logging
        if ((_b = config.logging) === null || _b === void 0 ? void 0 : _b.driver) {
            args.push('--log-driver', config.logging.driver);
            if (config.logging.options) {
                for (const [k, v] of Object.entries(config.logging.options)) {
                    args.push('--log-opt', `${k}=${v}`);
                }
            }
        }
        // security
        if ((_c = config.security) === null || _c === void 0 ? void 0 : _c.privileged) {
            args.push('--privileged');
        }
        if ((_d = config.security) === null || _d === void 0 ? void 0 : _d.capAdd) {
            for (const cap of config.security.capAdd) {
                args.push('--cap-add', cap);
            }
        }
        if ((_e = config.security) === null || _e === void 0 ? void 0 : _e.capDrop) {
            for (const cap of config.security.capDrop) {
                args.push('--cap-drop', cap);
            }
        }
        if ((_f = config.security) === null || _f === void 0 ? void 0 : _f.noNewPrivileges) {
            args.push('--security-opt', 'no-new-privileges');
        }
        if ((_g = config.security) === null || _g === void 0 ? void 0 : _g.apparmor) {
            args.push('--security-opt', `apparmor=${config.security.apparmor}`);
        }
        // network
        if (config.networkMode && typeof config.networkMode === 'string') {
            args.push('--network', config.networkMode);
        }
        // extra hosts
        if (config.extraHosts) {
            for (const host of config.extraHosts) {
                if (typeof host === 'string') {
                    args.push('--add-host', host);
                }
                else {
                    args.push('--add-host', `${host.host}:${host.ip}`);
                }
            }
        }
        // sysctls
        if (config.sysctls) {
            for (const [k, v] of Object.entries(config.sysctls)) {
                args.push('--sysctl', `${k}=${v}`);
            }
        }
        // stop signal / timeout
        if ((_h = config.stop) === null || _h === void 0 ? void 0 : _h.signal) {
            args.push('--stop-signal', config.stop.signal);
        }
        if (((_j = config.stop) === null || _j === void 0 ? void 0 : _j.gracePeriodSec) !== undefined) {
            args.push('--stop-timeout', String(config.stop.gracePeriodSec));
        }
        // resources
        if ((_k = config.resources) === null || _k === void 0 ? void 0 : _k.cpus) {
            args.push('--cpus', String(config.resources.cpus));
        }
        if ((_l = config.resources) === null || _l === void 0 ? void 0 : _l.memory) {
            args.push('--memory', String(config.resources.memory));
        }
        if ((_m = config.resources) === null || _m === void 0 ? void 0 : _m.shmSize) {
            args.push('--shm-size', String(config.resources.shmSize));
        }
        // image
        if (!config.image) {
            throw new Error('ContainerConfig.image is required for docker run');
        }
        args.push(config.image);
        // command override
        if (config.command) {
            if (Array.isArray(config.command)) {
                args.push(...config.command);
            }
            else {
                args.push(config.command);
            }
        }
        // entrypoint override
        if (config.entrypoint) {
            if (Array.isArray(config.entrypoint)) {
                args.unshift('--entrypoint', config.entrypoint.join(' '));
            }
            else {
                args.unshift('--entrypoint', config.entrypoint);
            }
        }
        return args.join(' ');
    }
    async networkList() {
        if (__classPrivateFieldGet(this, _DockerManager_dockerode, "f")) {
            const networks = await __classPrivateFieldGet(this, _DockerManager_dockerode, "f").listNetworks();
            return networks.map(net => ({
                name: net.Name,
                id: net.Id,
                driver: net.Driver,
                scope: net.Scope,
            }));
        }
        // docker network ls
        try {
            const { stdout } = await __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_exec).call(this, `network ls --format "{{.Name}};{{.ID}};{{.Driver}};{{.Scope}}"`);
            return stdout
                .split('\n')
                .filter(line => line.trim() !== '')
                .map(line => {
                const [name, id, driver, scope] = line.split(';');
                return { name, id, driver: driver, scope };
            });
        }
        catch (e) {
            this.log.debug(`Cannot list networks: ${e.message.toString()}`);
            return [];
        }
    }
    async networkCreate(name, driver) {
        if (__classPrivateFieldGet(this, _DockerManager_dockerode, "f")) {
            const net = await __classPrivateFieldGet(this, _DockerManager_dockerode, "f").createNetwork({ Name: name, Driver: driver || 'bridge' });
            const networks = await this.networkList();
            if (!networks.find(it => it.name === name)) {
                throw new Error(`Network ${name} not found after creation`);
            }
            return { stdout: `Network ${net.id} created`, stderr: '', networks };
        }
        const result = await __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_exec).call(this, `network create ${driver ? `--driver ${driver}` : ''} ${name}`);
        const networks = await this.networkList();
        if (!networks.find(it => it.name === name)) {
            throw new Error(`Network ${name} not found after creation`);
        }
        return Object.assign(Object.assign({}, result), { networks });
    }
    async networkRemove(networkId) {
        if (__classPrivateFieldGet(this, _DockerManager_dockerode, "f")) {
            const network = __classPrivateFieldGet(this, _DockerManager_dockerode, "f").getNetwork(networkId);
            await network.remove();
            const networks = await this.networkList();
            if (networks.find(it => it.id === networkId || it.name === networkId)) {
                throw new Error(`Network ${networkId} still found after deletion`);
            }
            return { stdout: `Network ${networkId} removed`, stderr: '', networks };
        }
        const result = await __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_exec).call(this, `network remove ${networkId}`);
        const networks = await this.networkList();
        if (networks.find(it => it.id === networkId)) {
            throw new Error(`Network ${networkId} still found after deletion`);
        }
        return Object.assign(Object.assign({}, result), { networks });
    }
    async networkPrune() {
        if (__classPrivateFieldGet(this, _DockerManager_dockerode, "f")) {
            const result = await __classPrivateFieldGet(this, _DockerManager_dockerode, "f").pruneNetworks();
            const networks = await this.networkList();
            return { stdout: `Networks pruned`, stderr: JSON.stringify(result), networks };
        }
        const result = await __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_exec).call(this, `network prune -f`);
        const networks = await this.networkList();
        return Object.assign(Object.assign({}, result), { networks });
    }
    /** List all volumes */
    async volumeList() {
        if (__classPrivateFieldGet(this, _DockerManager_dockerode, "f")) {
            const volumesData = await __classPrivateFieldGet(this, _DockerManager_dockerode, "f").listVolumes();
            return (volumesData.Volumes || []).map(vol => ({
                name: vol.Name,
                driver: vol.Driver,
                volume: vol.Mountpoint,
            }));
        }
        // docker network ls
        try {
            const { stdout } = await __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_exec).call(this, `volume ls --format "{{.Name}};{{.Driver}};{{.Mountpoint}}"`);
            return stdout
                .split('\n')
                .filter(line => line.trim() !== '')
                .map(line => {
                const [name, driver, volume] = line.split(';');
                return { name, driver: driver, volume };
            });
        }
        catch (e) {
            this.log.debug(`Cannot list networks: ${e.message.toString()}`);
            return [];
        }
    }
    async volumeCopyTo(volumeName, sourcePath) {
        const tempContainerName = `iobroker_temp_copy_${Date.now()}`;
        if (__classPrivateFieldGet(this, _DockerManager_dockerode, "f")) {
            // Check if the alpine image is there
            const images = await this.imageList();
            if (!images.find(img => img.repository === 'alpine')) {
                const pullResult = await this.imagePull('alpine');
                if (pullResult.stderr) {
                    return { stdout: '', stderr: `Cannot pull alpine image: ${pullResult.stderr}` };
                }
            }
            // create a temporary container with volume mounted
            const container = await __classPrivateFieldGet(this, _DockerManager_dockerode, "f").createContainer({
                Image: 'alpine',
                name: tempContainerName,
                Cmd: ['sleep', '30'],
                HostConfig: {
                    Binds: [`${volumeName}:/data`],
                    AutoRemove: true,
                },
            });
            try {
                await container.start();
                // Lazy loading tar-fs
                if (!__classPrivateFieldGet(this, _DockerManager_tarPack, "f")) {
                    await import('tar-fs')
                        .then(tarFs => (__classPrivateFieldSet(this, _DockerManager_tarPack, tarFs.default.pack, "f")))
                        .catch(e => this.log.error(`Cannot import tar-fs package: ${e.message}`));
                }
                if (!__classPrivateFieldGet(this, _DockerManager_tarPack, "f")) {
                    throw new Error('Cannot load tar-fs package');
                }
                // use dockerode to copy files
                const pack = __classPrivateFieldGet(this, _DockerManager_tarPack, "f").call(this, sourcePath);
                await container.putArchive(pack, { path: '/data' });
                return { stdout: 'Data copied to volume', stderr: '' };
            }
            catch (e) {
                return { stdout: '', stderr: `Cannot copy data to volume: ${e.message}` };
            }
            finally {
                // remove temporary container
                try {
                    await container.stop();
                    await container.remove({ force: true });
                }
                catch (e) {
                    if (!e.message.includes('already stopped')) {
                        this.log.warn(`Cannot remove temporary container ${tempContainerName}: ${e.message}`);
                    }
                }
            }
        }
        // create a temporary container with volume mounted
        const createResult = await __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_exec).call(this, `create --rm -v ${volumeName}:/data --name ${tempContainerName} alpine sleep 60`);
        if (createResult.stderr) {
            return { stdout: '', stderr: `Cannot create temporary container: ${createResult.stderr}` };
        }
        try {
            const copyResult = await __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_exec).call(this, `cp ${sourcePath} ${tempContainerName}:/data/`);
            if (copyResult.stderr) {
                return { stdout: '', stderr: `Cannot copy data to volume: ${copyResult.stderr}` };
            }
            return { stdout: 'Data copied to volume', stderr: '' };
        }
        finally {
            // remove temporary container
            await __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_exec).call(this, `rm -f ${tempContainerName}`);
        }
    }
    /** List files in a volume */
    async volumeDir(volumeName, path) {
        try {
            // Execute `docker run --rm -it -v {volumeName}:/data alpine ls -l /data/{path}
            const result = await this.containerRun({
                image: 'alpine',
                name: `iobroker_temp_ls_${Date.now()}`,
                removeOnExit: true,
                tty: false,
                stdinOpen: false,
                command: ['ls', '-la', path ? (0, node_path_1.join)('/data', path || '').replace(/\\/g, '/') : '/data'],
                mounts: [{ type: 'volume', source: volumeName, target: '/data', readOnly: true }],
            });
            if (!result || result.stderr) {
                this.log.error(`Cannot list files in volume ${volumeName}: ${(result === null || result === void 0 ? void 0 : result.stderr) || 'unknown error'}`);
                return (result === null || result === void 0 ? void 0 : result.stderr) || 'unknown error';
            }
            // parse result.stdout
            if (!result.stdout) {
                return [];
            }
            return parseLsLong(result.stdout);
        }
        catch (e) {
            this.log.error(`Cannot list files in volume ${volumeName}: ${e.message}`);
            return e.message;
        }
    }
    /** Read a text file from a volume */
    async volumeFile(volumeName, filePath) {
        try {
            // Execute `docker run --rm -it -v {volumeName}:/data alpine cat /data/{filePath}
            const result = await this.containerRun({
                image: 'alpine',
                name: `iobroker_temp_cat_${Date.now()}`,
                removeOnExit: true,
                tty: false,
                stdinOpen: false,
                command: ['cat', (0, node_path_1.join)('/data', filePath).replace(/\\/g, '/')],
                mounts: [{ type: 'volume', source: volumeName, target: '/data', readOnly: true }],
            });
            if (!result || result.stderr) {
                this.log.error(`Cannot read file ${filePath} in volume ${volumeName}: ${(result === null || result === void 0 ? void 0 : result.stderr) || 'unknown error'}`);
                throw new Error(result === null || result === void 0 ? void 0 : result.stderr);
            }
            return result.stdout || '';
        }
        catch (e) {
            this.log.error(`Cannot read file ${filePath} in volume ${volumeName}: ${e.message}`);
            throw e;
        }
    }
    /**
     * Create a volume
     *
     * @param name Volume name
     * @param driver Volume driver
     * @param volume Volume options (depends on a driver)
     */
    async volumeCreate(name, driver, volume) {
        if (__classPrivateFieldGet(this, _DockerManager_dockerode, "f")) {
            const vol = await __classPrivateFieldGet(this, _DockerManager_dockerode, "f").createVolume({
                Name: name,
                Driver: driver || 'local',
                DriverOpts: volume ? { device: volume, o: 'bind', type: 'none' } : {},
            });
            const volumes = await this.volumeList();
            if (!volumes.find(it => it.name === name)) {
                throw new Error(`Volume ${name} not found after creation`);
            }
            return { stdout: `Volume ${vol.Name} created`, stderr: '', volumes };
        }
        let result;
        if (driver === 'local' || !driver) {
            if (volume) {
                result = await __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_exec).call(this, `volume create local --opt type=none --opt device=${volume} --opt o=bind ${name}`);
            }
            else {
                result = await __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_exec).call(this, `volume create ${name}`);
            }
        }
        else {
            throw new Error('not implemented');
        }
        const volumes = await this.volumeList();
        if (!volumes.find(it => it.name === name)) {
            throw new Error(`Volume ${name} not found after creation`);
        }
        return Object.assign(Object.assign({}, result), { volumes });
    }
    /** Remove a volume */
    async volumeRemove(volumeName) {
        if (__classPrivateFieldGet(this, _DockerManager_dockerode, "f")) {
            const volume = __classPrivateFieldGet(this, _DockerManager_dockerode, "f").getVolume(volumeName);
            await volume.remove();
            const volumes = await this.volumeList();
            if (volumes.find(it => it.name === volumeName)) {
                throw new Error(`Volume ${volumeName} still found after deletion`);
            }
            return { stdout: `Volume ${volumeName} removed`, stderr: '', volumes };
        }
        const result = await __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_exec).call(this, `volume remove ${volumeName}`);
        const volumes = await this.volumeList();
        if (volumes.find(it => it.name === volumeName)) {
            throw new Error(`Volume ${volumeName} still found after deletion`);
        }
        return Object.assign(Object.assign({}, result), { volumes });
    }
    /** Prune unused volumes */
    async volumePrune() {
        if (__classPrivateFieldGet(this, _DockerManager_dockerode, "f")) {
            const result = await __classPrivateFieldGet(this, _DockerManager_dockerode, "f").pruneVolumes();
            const volumes = await this.volumeList();
            return { stdout: `Volumes pruned`, stderr: JSON.stringify(result), volumes };
        }
        const result = await __classPrivateFieldGet(this, _DockerManager_instances, "m", _DockerManager_exec).call(this, `volume prune -f`);
        const volumes = await this.volumeList();
        return Object.assign(Object.assign({}, result), { volumes });
    }
    /** Stop own containers if necessary */
    destroy() {
        return Promise.resolve();
    }
}
_DockerManager_waitReady = new WeakMap(), _DockerManager_driver = new WeakMap(), _DockerManager_dockerode = new WeakMap(), _DockerManager_cliAvailable = new WeakMap(), _DockerManager_tarPack = new WeakMap(), _DockerManager_instances = new WeakSet(), _DockerManager_isDockerDaemonRunning = async function _DockerManager_isDockerDaemonRunning() {
    if (__classPrivateFieldGet(this, _DockerManager_dockerode, "f")) {
        return true;
    }
    try {
        const { stdout, stderr } = await execPromise('systemctl status docker');
        // ● docker.service - Docker Application Container Engine
        //      Loaded: loaded (/lib/systemd/system/docker.service; enabled; preset: enabled)
        //      Active: active (running) since Fri 2025-08-15 08:37:22 CEST; 3 weeks 2 days ago
        // TriggeredBy: ● docker.socket
        //        Docs: https://docs.docker.com
        //    Main PID: 785 (dockerd)
        //       Tasks: 30
        //         CPU: 4min 17.003s
        //      CGroup: /system.slice/docker.service
        //              ├─  785 /usr/bin/dockerd -H fd:// --containerd=/run/containerd/containerd.sock
        //              ├─97032 /usr/bin/docker-proxy -proto tcp -host-ip 0.0.0.0 -host-port 5000 -container-ip 172.17.0.2 -container-port 5000 -use-listen-fd
        //              └─97039 /usr/bin/docker-proxy -proto tcp -host-ip :: -host-port 5000 -container-ip 172.17.0.2 -container-port 5000 -use-listen-fd
        if ((stderr === null || stderr === void 0 ? void 0 : stderr.includes('could not be found')) || (stderr === null || stderr === void 0 ? void 0 : stderr.includes('not-found'))) {
            this.log.error(`Docker is not installed: ${stderr}`);
            return false;
        }
        return stdout.includes('(running)');
    }
    catch (_a) {
        return false;
    }
}, _DockerManager_exec = function _DockerManager_exec(command) {
    if (!this.installed) {
        return Promise.reject(new Error('Docker is not installed'));
    }
    const finalCommand = this.needSudo ? `sudo docker ${command}` : `docker ${command}`;
    return execPromise(finalCommand);
}, _DockerManager_isDockerInstalled = async function _DockerManager_isDockerInstalled() {
    if (__classPrivateFieldGet(this, _DockerManager_driver, "f") === 'cli') {
        try {
            const result = await execPromise('docker --version');
            if (!result.stderr && result.stdout) {
                // "Docker version 28.3.2, build 578ccf6\n"
                return result.stdout.split('\n')[0].trim();
            }
            this.log.debug(`Docker not installed: ${result.stderr}`);
        }
        catch (e) {
            this.log.debug(`Docker not installed: ${e.message}`);
        }
    }
    else if (__classPrivateFieldGet(this, _DockerManager_dockerode, "f")) {
        try {
            const info = await __classPrivateFieldGet(this, _DockerManager_dockerode, "f").version();
            if (info === null || info === void 0 ? void 0 : info.Version) {
                return `Docker version ${info.Version}, api version: ${info.ApiVersion}`;
            }
        }
        catch (e) {
            this.log.debug(`Docker not installed: ${e.message}`);
        }
    }
    return false;
}, _DockerManager_isNeedSudo = async function _DockerManager_isNeedSudo() {
    try {
        await execPromise('docker ps');
        return false;
    }
    catch (_a) {
        return true;
    }
}, _DockerManager_parseSize = function _DockerManager_parseSize(sizeStr) {
    const units = {
        B: 1,
        KB: 1024,
        MB: 1024 * 1024,
        GB: 1024 * 1024 * 1024,
        TB: 1024 * 1024 * 1024 * 1024,
    };
    const match = sizeStr.match(/^([\d.]+)([KMGTP]?B)$/);
    if (match) {
        const value = parseFloat(match[1]);
        const unit = match[2];
        return value * (units[unit] || 1);
    }
    return 0;
};
exports.default = DockerManager;
